import { createI18n } from "vue-i18n";
import { messages, getBrowserLocale } from "../shared/i18n/locales";
import { logger } from "../infrastructure/services/LoggerService";
const AVAILABLE_LOCALES = ["en-US", "fr-FR", "nl-NL"];
function isLocaleSupported(locale) {
    return AVAILABLE_LOCALES.includes(locale);
}
function getInitialLocale() {
    const savedLocale = localStorage.getItem("locale");
    if (savedLocale && isLocaleSupported(savedLocale)) {
        return savedLocale;
    }
    const browserLocale = getBrowserLocale();
    if (browserLocale.startsWith("fr"))
        return "fr-FR";
    if (browserLocale.startsWith("en"))
        return "en-US";
    if (browserLocale.startsWith("nl"))
        return "nl-NL";
    return "en-US";
}
const mappedMessages = {
    "en-US": messages.en,
    "fr-FR": messages.fr,
    "nl-NL": messages.nl,
    // Bare ISO stems too, pointing at the SAME bundles. The embedded donation-form engine's chrome
    // (`useChrome.tf()`, `StepProgress.vue`) forces vue-i18n's per-call `{ locale }` option to
    // `ctx.lang` — the engine's OWN active locale, which is always a bare stem ('fr'/'nl'/'en',
    // mirroring `DonationFormDocument.defaultLocale`/`supportedLocales`), NEVER this host's regional
    // tag. Without a bundle registered under that exact bare key, vue-i18n can't resolve the override
    // and silently renders `fallbackLocale` ("en-US") instead — the live preview showed English chrome
    // for a French-only form. Mirrors eudonet-opublic-front's `shared/i18n/index.ts`, which keys its
    // messages by bare stem for the same reason.
    en: messages.en,
    fr: messages.fr,
    nl: messages.nl,
    "fr-CA": messages["fr-CA"],
};
/**
 * CONSTRAINT: zero is not plural in every locale, and vue-i18n's built-in rule assumes it is.
 * With two branches it hard-codes `choice ? (choice > 1 ? 1 : 0) : 1`, sending 0 to the PLURAL
 * branch whatever the locale. CLDR puts 0 in `one` for French ("0 action") and in `other` for
 * English and Dutch ("0 actions" / "0 acties"), so every `singular | plural` message in the
 * bundles read "0 actions", "0 membres", "0 jours restants" (OP-7088).
 *
 * Only the two-branch case is locale-dependent. Anything else is handed back to `orgRule` —
 * vue-i18n's own rule, which it passes here precisely because a custom one is registered — so
 * branch indices for other arities cannot drift from the library's.
 */
function pluralRuleFor(locale) {
    let selector;
    return (choice, choicesLength, orgRule) => {
        if (choicesLength === 2) {
            selector ?? (selector = new Intl.PluralRules(locale));
            return selector.select(Math.abs(choice)) === "one" ? 0 : 1;
        }
        if (!orgRule) {
            throw new Error(`vue-i18n supplied no default plural rule for locale "${locale}".`);
        }
        return orgRule(choice, choicesLength);
    };
}
const i18n = createI18n({
    locale: getInitialLocale(),
    fallbackLocale: "en-US",
    messages: mappedMessages,
    // Every key of `mappedMessages`, bare stems included: a rule is looked up by the EXACT resolved
    // locale, so omitting `fr` would leave the donation-form engine's `{ locale: "fr" }` overrides on
    // the built-in English-shaped rule.
    pluralRules: Object.fromEntries(Object.keys(mappedMessages).map((locale) => [locale, pluralRuleFor(locale)])),
    legacy: false,
    globalInjection: true,
    warnHtmlMessage: false,
});
/**
 * Normalize any locale variant to the supported canonical form.
 * e.g. "fr-BE", "fr-CA" → "fr-FR" / "nl-BE" → "nl-NL" / "en-GB" → "en-US"
 */
function normalizeLocale(locale) {
    if (locale.startsWith("fr"))
        return "fr-FR";
    if (locale.startsWith("nl"))
        return "nl-NL";
    if (locale.startsWith("en"))
        return "en-US";
    return null;
}
export function setLocale(locale) {
    const canonical = isLocaleSupported(locale) ? locale : normalizeLocale(locale);
    if (canonical) {
        i18n.global.locale.value = canonical;
        localStorage.setItem("locale", canonical);
        document.documentElement.lang = canonical;
        logger.debug(`i18n locale set to: ${canonical} (from: ${locale})`);
    }
    else {
        logger.debug(`Locale ${locale} is not supported in i18n messages`);
    }
}
export default i18n;
